import type { KeyboardEvent } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { cn } from './utils';

/**
 * Minimal table wrapper around TanStack Table. Stays primitive on
 * purpose — pagination/sorting belong to the route (URL state) rather
 * than the table component, so this stays reusable for any read view.
 *
 * `onRowClick` is opt-in; rows become interactive (focusable, keyboard
 * activatable) only when a handler is provided — a table with no
 * `onRowClick` renders plain, inert `<tr>`s.
 *
 * Below 640px each row renders as a stacked, labeled card (first column
 * becomes the card title) per the design-system DataTable. Pass
 * `stack={false}` to keep the horizontally-scrolling table on phones. The
 * stacked card gets the same keyboard treatment as the desktop row.
 */
const ROW_FOCUS_CLASS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary';

// Enter and Space both activate the row, matching native button/link
// behavior; Space additionally needs preventDefault so it doesn't also
// scroll the page (the default behavior for Space on a focusable,
// non-form element).
function onRowKeyDown<T>(row: T, onRowClick: (row: T) => void) {
  return (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      onRowClick(row);
    } else if (e.key === ' ') {
      e.preventDefault();
      onRowClick(row);
    }
  };
}
export function DataTable<T>({
  data,
  columns,
  onRowClick,
  emptyMessage,
  className,
  stack = true,
}: {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  stack?: boolean;
}) {
  // Append a chevron column when rows are clickable so users have a clear
  // affordance that the row navigates somewhere.
  const chevronCol: ColumnDef<T, unknown> = {
    id: '__chevron__',
    header: '',
    size: 32,
    cell: () => (
      <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
    ),
  };
  const effectiveCols = onRowClick ? [...columns, chevronCol] : columns;

  const table = useReactTable<T>({
    data,
    columns: effectiveCols,
    getCoreRowModel: getCoreRowModel(),
  });

  // dt labels for stacked cards — string headers only (component headers
  // fall back to the column id).
  const headerLabel = (c: ColumnDef<T, unknown>) =>
    typeof c.header === 'string' ? c.header : (c.id ?? '');

  const rows = table.getRowModel().rows;

  return (
    <>
    <div
      className={cn(
        'overflow-x-auto rounded-lg border border-border bg-card',
        stack && 'hidden sm:block',
        className,
      )}
    >
      <table className="min-w-full text-sm">
        <thead className="border-b border-border bg-muted">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground"
                  style={{ width: h.getSize() === 150 ? undefined : h.getSize() }}
                >
                  {h.isPlaceholder
                    ? null
                    : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-10 text-center text-sm text-muted-foreground"
              >
                {emptyMessage ?? 'No rows.'}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((r) => (
              <tr
                key={r.id}
                onClick={onRowClick ? () => onRowClick(r.original) : undefined}
                onKeyDown={onRowClick ? onRowKeyDown(r.original, onRowClick) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                // NO role override here. A <tr>'s implicit role is `row`, and
                // its <td> children are only valid inside one — setting
                // role="button" invalidates the table structure and makes
                // assistive tech flatten the row into a single button, losing
                // per-cell announcement and table navigation. tabIndex +
                // onKeyDown give keyboard operability while the row stays a
                // row. (The sub-640px stacked card IS a <div>, so role="button"
                // is correct there and is left alone.)
                className={cn(
                  'group border-b border-border last:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-accent',
                  onRowClick && ROW_FOCUS_CLASS,
                )}
              >
                {r.getVisibleCells().map((c) => (
                  <td key={c.id} className="px-4 py-3 align-middle">
                    {flexRender(c.column.columnDef.cell, c.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    {stack && (
      <div className="flex flex-col gap-2.5 sm:hidden">
        {rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            {emptyMessage ?? 'No rows.'}
          </div>
        ) : (
          rows.map((r) => {
            const cells = r
              .getVisibleCells()
              .filter((c) => c.column.id !== '__chevron__');
            const [title, ...fields] = cells;
            return (
              <div
                key={r.id}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(r.original) : undefined}
                onKeyDown={onRowClick ? onRowKeyDown(r.original, onRowClick) : undefined}
                className={cn(
                  'rounded-md border border-border bg-muted/40 px-3.5 py-3',
                  onRowClick && 'cursor-pointer active:bg-muted/70',
                  onRowClick && ROW_FOCUS_CLASS,
                )}
              >
                <div className="mb-2 text-sm font-semibold text-foreground">
                  {title &&
                    flexRender(title.column.columnDef.cell, title.getContext())}
                </div>
                <dl>
                  {fields.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-baseline justify-between gap-4 border-b border-border/40 py-1.5 last:border-b-0"
                    >
                      <dt className="flex-none text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {headerLabel(c.column.columnDef)}
                      </dt>
                      <dd className="min-w-0 text-right text-sm text-foreground">
                        {flexRender(c.column.columnDef.cell, c.getContext())}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })
        )}
      </div>
    )}
    </>
  );
}
